import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/apiClient';
import { formControlClass, formPrimaryButtonClass } from '../components/ui/formUiClasses';

const validatePassword = (password, t) => {
  if (!password) return t('common.passwordRequired');
  if (password.length < 8) return t('auth.resetPassword.passwordRules');
  const types = [
    /[A-Z]/.test(password),
    /[a-z]/.test(password),
    /\d/.test(password),
    /[@$!%*?&]/.test(password)
  ].filter(Boolean).length;
  if (types < 3) return t('auth.resetPassword.passwordRules');
  return '';
};

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError(t('auth.resetPassword.missingToken'));
      return;
    }

    const passwordError = validatePassword(password, t);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError(t('common.passwordsDoNotMatch'));
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/api/auth/reset-password', {
        token,
        password
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || t('auth.resetPassword.failed'));
      }
      navigate('/login', { replace: true, state: { passwordReset: true } });
    } catch (err) {
      setError(err.message || t('auth.resetPassword.failed'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="h-full flex items-center justify-center bg-canvas py-12 px-4">
        <div className="max-w-md w-full bg-surface p-8 rounded-lg shadow-md space-y-4 text-center">
          <p className="text-sm text-ink-muted">{t('auth.resetPassword.missingToken')}</p>
          <Link to="/forgot-password" className="text-sm font-medium text-link hover:text-link-hover">
            {t('auth.resetPassword.requestNewLink')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex items-center justify-center bg-canvas py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-10 text-center text-3xl font-extrabold text-ink">
            {t('auth.resetPassword.title')}
          </h2>
          <p className="mt-2 text-center text-sm text-ink-muted">
            {t('auth.resetPassword.description')}
          </p>
        </div>

        <form className="mt-8 space-y-6 bg-surface p-8 rounded-lg shadow-md" onSubmit={handleSubmit} noValidate>
          <div className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-ink-muted mb-1">
                {t('common.password')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className={formControlClass}
                placeholder={t('common.enterPassword')}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError('');
                }}
              />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-ink-muted mb-1">
                {t('common.confirmPassword')}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                className={formControlClass}
                placeholder={t('common.confirmYourPassword')}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (error) setError('');
                }}
              />
            </div>
            <p className="text-xs text-ink-subtle">{t('auth.resetPassword.passwordRules')}</p>
          </div>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md" role="alert">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full ${formPrimaryButtonClass}`}
          >
            {loading ? t('common.loading') : t('auth.resetPassword.submit')}
          </button>

          <p className="text-center text-sm text-ink-subtle">
            <Link to="/forgot-password" className="font-medium text-link hover:text-link-hover">
              {t('auth.resetPassword.requestNewLink')}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
